import { ChevronDownIcon } from '@chakra-ui/icons'
import {
  Avatar,
  Badge,
  Box,
  Button,
  Flex,
  IconButton,
  Image,
  Input,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useDisclosure
} from '@chakra-ui/react'
import { AxiosError } from 'axios'
import { useEffect, useMemo, useRef, useState } from 'react'

import { ConfirmDialog } from '../../../../components/ConfirmDialog'
import DateBlurInput from '../../../../components/DateBlurInput'
import { EMPTY_CELL_VALUE, EmptyCell } from '../../../../components/EmptyCell'
import { FormField } from '../../../../components/FormField'
import { Icon } from '../../../../components/Icon'
import MoneyBlurInput from '../../../../components/MoneyBlurInput'
import Paginator, { DEFAULT_PAGE_SIZE } from '../../../../components/paginator'
import PickupInfoModal from '../../../../components/PickupInfoModal'
import ArtworkPreviewModal from '../../../../components/previewModal/ArtworkPreviewModal'
import { SearchableSelect } from '../../../../components/Select'
import { DELIVERY_STATUS, EXHIBITION_STATUS, StatusBadge, statusOptions } from '../../../../components/StatusBadge'
import { Tooltip } from '../../../../components/Tooltip'
import {
  formatApiDate,
  formatCurrency,
  formatCurrencyValue,
  formatIsoDate,
  formatWithdrawWindow,
  useAppToast,
  useHasRole
} from '../../../../helpers'
import { getArtworkSaleStatus } from '../../../../helpers/artworkSaleStatus'
import ContestsService from '../../../../services/Contests/ContestsService'
import { IContestArtwork, IContestArtworksFilter, IContestForSelect } from '../../../../services/Contests/interfaces'
import { resolveArtMeta } from '../../Contests/artFieldHelpers'
import ContestsSendExhibitorCertificatesModal from '../ContestsSendExhibitorCertificatesModal'
import ContestsSendPickupEmailsModal from '../ContestsSendPickupEmailsModal'

interface IArtworksTabProps {
  /** Concurso escolhido, que mora na página: a aba é desmontada pelo `isLazy` ao trocar de aba. */
  contestName: string
  onContestFilterChange?: (contestName: string) => void
}

const saleStatusOptions = [
  { value: 'available', label: 'Em leilão' },
  { value: 'sold', label: 'Vendida' },
  { value: 'unsold', label: 'Não Vendida' }
]

const pickupEmailStatusOptions = [
  { value: 'sent', label: 'Enviado' },
  { value: 'pending', label: 'Pendente' }
]

const updateArtExhibitionStatusMessages: Record<string, string> = {
  approved: 'Obra aprovada com sucesso!',
  rejected: 'Obra reprovada com sucesso!',
  pending: 'Obra voltou para status pendente!',
  stand_by: 'Obra movida para Stand-by!'
}

/**
 * ABRA-147 — whether the pickup notice already went out for this artwork.
 * "Pendente" is only meaningful while the artwork is still at ABRA: one that was
 * never received, or already returned, is not waiting for a notice at all.
 */
function renderPickupEmailStatus(art: IContestArtwork) {
  if (art.pickup_email_sent_at) {
    // The API sends "YYYY-MM-DD HH:mm"; tolerate an ISO "T" like the rest of the screen.
    const [date, time] = art.pickup_email_sent_at.replace('T', ' ').split(' ')

    const sentLabel = `Aviso enviado em ${formatIsoDate(date)}${time ? ` às ${time}` : ''}`

    return (
      <Tooltip label={sentLabel}>
        <Badge colorScheme="green" tabIndex={0} aria-label={sentLabel}>
          Enviado
        </Badge>
      </Tooltip>
    )
  }

  const isAtAbra = !!art.receipt_date && !art.return_date

  if (!isAtAbra) {
    return <EmptyCell />
  }

  return (
    <Tooltip label="A obra está na ABRA e ainda não foi avisada">
      <Badge colorScheme="gray" tabIndex={0} aria-label="A obra está na ABRA e ainda não foi avisada">
        Pendente
      </Badge>
    </Tooltip>
  )
}

/**
 * Component for displaying return date (read-only) with double-click to view pickup info
 */
function ReturnDateDisplay({
  returnDate,
  artId,
  onPickupInfoOpen
}: {
  returnDate: string | null
  artId: number
  onPickupInfoOpen: (artId: number) => void
}) {
  const [clickCount, setClickCount] = useState(0)
  const clickTimeoutRef = useRef<NodeJS.Timeout>()

  const handleDoubleClick = () => {
    if (clickTimeoutRef.current) {
      clearTimeout(clickTimeoutRef.current)
    }
    setClickCount(0)
    onPickupInfoOpen(artId)
  }

  const handleClick = () => {
    setClickCount((prev) => prev + 1)

    if (clickTimeoutRef.current) {
      clearTimeout(clickTimeoutRef.current)
    }

    clickTimeoutRef.current = setTimeout(() => {
      setClickCount(0)
    }, 300)
  }

  useEffect(() => {
    if (clickCount === 2) {
      handleDoubleClick()
    }
  }, [clickCount])

  useEffect(() => {
    return () => {
      if (clickTimeoutRef.current) {
        clearTimeout(clickTimeoutRef.current)
      }
    }
  }, [])

  const formattedDate = formatApiDate(returnDate) || EMPTY_CELL_VALUE

  return (
    <Flex
      alignItems="center"
      justifyContent="space-between"
      cursor="pointer"
      padding={1}
      borderRadius="md"
      _hover={{ bg: 'gray.100' }}
      onClick={handleClick}
      transition="background-color 0.2s"
    >
      <Text fontSize="xs" userSelect="none">
        {formattedDate}
      </Text>
      <Tooltip label="Clique para ver dados de retirada">
        <IconButton
          icon={<Icon name="lni lni-information" size="sm" />}
          size="xs"
          variant="ghost"
          colorScheme="gray"
          aria-label="Ver dados de retirada"
          onClick={(e) => {
            e.stopPropagation()
            onPickupInfoOpen(artId)
          }}
        />
      </Tooltip>
    </Flex>
  )
}

export default function ArtworksTab({ contestName, onContestFilterChange }: IArtworksTabProps) {
  const toast = useAppToast()
  const pickupInfoModal = useDisclosure()
  const pickupEmailsModal = useDisclosure()
  const exhibitorCertificatesModal = useDisclosure()
  const hasRole = useHasRole()
  const contestService = useMemo(() => new ContestsService(), [])
  const [artworks, setArtworks] = useState<IContestArtwork[]>([])
  const [contests, setContests] = useState<IContestForSelect[]>([])
  const [selectedContestId, setSelectedContestId] = useState<number | null>(null)
  const [totalPages, setTotalPages] = useState(1)
  const [selectedArtworkIndex, setSelectedArtworkIndex] = useState<number | null>(null)
  const [selectedArtIdForPickup, setSelectedArtIdForPickup] = useState<number>(0)
  const [selectedArtReturnDateForPickup, setSelectedArtReturnDateForPickup] = useState<string | null>(null)
  const [deletingArtId, setDeletingArtId] = useState<number>(0)
  const [openingPdfId, setOpeningPdfId] = useState<number | null>(null)
  const [pendingReceiptEmailArt, setPendingReceiptEmailArt] = useState<IContestArtwork | null>(null)
  const receiptEmailModal = useDisclosure()
  const [filter, setFilter] = useState<IContestArtworksFilter>({
    contest_name: contestName,
    applicant_name: '',
    exhibition_status: '',
    delivery_status: '',
    sale_status: '',
    available_for_auction: '',
    pickup_email_status: '',
    page: 1,
    page_size: DEFAULT_PAGE_SIZE
  })

  // The listing filters by name, but contest names are not unique — the pickup
  // emails endpoints must address the contest by id.
  const selectedContest = useMemo(() => contests.find((c) => c.id === selectedContestId), [contests, selectedContestId])

  const fetchArtworks = async () => {
    try {
      const response = await contestService.listArtworks(filter)
      setArtworks(response.data)
      setTotalPages(Math.ceil(response.total_filtered / filter.page_size))
    } catch {
      toast.error('Erro ao Listar Obras!')
    }
  }

  useEffect(() => {
    contestService
      .getAllForSelect()
      .then((response) => setContests(response))
      .catch(() => toast.error('Erro ao Listar Concursos!'))
  }, [])

  // A aba filtra por nome, mas o select e os e-mails de retirada endereçam o concurso por id.
  // Quem chega pela outra aba traz só o nome, então o id tem que ser recuperado quando a lista
  // de concursos volta da API — sem isto o campo aparece vazio sobre uma tabela já filtrada, e
  // o botão de e-mails de retirada, que depende do id, some.
  useEffect(() => {
    if (!contestName || contests.length === 0) return

    const matched = contests.find((contest) => contest.name === contestName)
    if (matched) setSelectedContestId(matched.id)
  }, [contestName, contests])

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchArtworks()
    }, 500)
    return () => clearTimeout(timeoutId)
  }, [filter])

  useEffect(() => {
    if (selectedArtworkIndex === null) return
    const handler = (event: KeyboardEvent) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault()
        setSelectedArtworkIndex((i) => {
          if (i === null) return i
          return Math.max(0, i - 1)
        })
      } else if (event.key === 'ArrowRight') {
        event.preventDefault()
        setSelectedArtworkIndex((i) => {
          if (i === null) return i
          return Math.min(artworks.length - 1, i + 1)
        })
      }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [selectedArtworkIndex, artworks.length])

  const handleExhibitionStatusChange = async (
    artId: number,
    status: 'approved' | 'rejected' | 'pending' | 'stand_by'
  ) => {
    try {
      await contestService.updateArtExhibitionStatus(artId, status)
      toast.success(updateArtExhibitionStatusMessages[status])
      fetchArtworks()
    } catch {
      toast.error('Erro ao atualizar status!')
    }
  }

  const handleReceiptDateUpdate = async (art: IContestArtwork, value: string) => {
    try {
      await contestService.updateArtFields(art.id, { receipt_date: value || null })
      toast.success('Data de recebimento atualizada com sucesso!')
      fetchArtworks()
      if (value) {
        setPendingReceiptEmailArt(art)
        receiptEmailModal.onOpen()
      }
    } catch {
      toast.error('Erro ao atualizar obra!')
    }
  }

  // Saving null clears the per-artwork override, so the artwork goes back to
  // inheriting the contest value (ABRA-191). Rethrows so MoneyBlurInput keeps
  // the pending value and retries on the next blur.
  const handleFrameValueUpdate = async (art: IContestArtwork, value: number | null) => {
    try {
      await contestService.updateArtFields(art.id, { frame_value: value })
      toast.success(value == null ? 'Valor da moldura restaurado para o do concurso!' : 'Valor da moldura atualizado!')
      fetchArtworks()
    } catch (error) {
      toast.error('Erro ao atualizar o valor da moldura!', error)
      throw error
    }
  }

  /**
   * The input binds to the override, not to the effective value: an empty field
   * with the contest value as placeholder is what tells the team "this artwork
   * is inheriting". Typing sets an override, clearing gives it back.
   */
  const renderFrameValue = (art: IContestArtwork) => {
    if (art.frame_value_mode === 'disabled' || !art.available_for_auction) {
      return <Text fontSize="xs">–</Text>
    }

    const inherited = art.frame_value == null
    const effective = art.effective_frame_value != null ? formatCurrencyValue(Number(art.effective_frame_value)) : null

    const input = (
      <MoneyBlurInput
        initialValue={art.frame_value != null ? Number(art.frame_value) : null}
        placeholder={effective ?? 'Sem valor'}
        ariaLabel={`Valor da moldura de ${art.name}`}
        onSave={(value) => handleFrameValueUpdate(art, value)}
      />
    )

    if (!inherited) return input

    return (
      <Tooltip
        label={
          effective ? 'Valor padrão do concurso. Digite para definir um valor só desta obra.' : 'Sem valor definido.'
        }
      >
        {/* Tooltip clones its child with a ref, and MoneyBlurInput is a plain
            function component — without this wrapper React drops the ref and
            warns in the console. */}
        <Box as="span">{input}</Box>
      </Tooltip>
    )
  }

  const handleSendReceiptEmail = async () => {
    if (!pendingReceiptEmailArt) return
    const artId = pendingReceiptEmailArt.id
    receiptEmailModal.onClose()
    setPendingReceiptEmailArt(null)
    try {
      await contestService.sendArtReceiptEmail(artId)
      toast.success('E-mail de recebimento enviado com sucesso!')
    } catch {
      toast.error('Erro ao enviar e-mail de recebimento!')
    }
  }

  const handleDeleteArt = async () => {
    try {
      await contestService.deleteArt(deletingArtId)
      toast.success('Obra removida com sucesso!')
      fetchArtworks()
    } catch {
      toast.error('Erro ao remover obra!')
    } finally {
      setDeletingArtId(0)
    }
  }

  const handleOpenArtworkIdentifierPdf = async (artId: number) => {
    setOpeningPdfId(artId)
    try {
      await contestService.printArtworkIdentifierPdf(artId)
    } catch (error) {
      const axiosError = error as AxiosError
      const status = axiosError?.response?.status

      let title = 'Erro ao gerar identificador!'

      if (status === 404) {
        title = 'Obra não encontrada.'
      } else if (axiosError?.response?.data instanceof Blob) {
        try {
          const text = await axiosError.response.data.text()
          const parsed = JSON.parse(text) as { message?: string }
          if (parsed?.message) {
            title = parsed.message
          }
        } catch {
          // Não foi possível parsear o Blob de erro — mantém o título padrão.
        }
      }

      toast.error(title, error)
    } finally {
      setOpeningPdfId(null)
    }
  }

  return (
    <>
      <Box mb={4}>
        <Flex alignItems="center" flexWrap="wrap" gap={2}>
          <FormField label="Concurso" minW="200px" flex="1">
            <SearchableSelect<number>
              isClearable
              placeholder="Selecione um concurso"
              entity="concurso"
              options={contests.map((contest) => ({ value: contest.id, label: contest.name }))}
              value={selectedContest?.id ?? ''}
              onChange={(contestId) => {
                const contestName = contests.find((contest) => contest.id === contestId)?.name ?? ''
                setSelectedContestId(contestId === '' ? null : contestId)
                setFilter({ ...filter, contest_name: contestName, page: 1 })
                onContestFilterChange?.(contestName)
              }}
            />
          </FormField>

          <FormField label="Participante" minW="200px" flex="1">
            <Input
              type="text"
              placeholder="Nome ou CPF"
              value={filter.applicant_name}
              onChange={(e) => setFilter({ ...filter, applicant_name: e.target.value, page: 1 })}
            />
          </FormField>

          <FormField label="Status Exposição" minW="180px" flex="1">
            <SearchableSelect
              isClearable
              placeholder="Todos"
              options={statusOptions(EXHIBITION_STATUS)}
              value={filter.exhibition_status}
              onChange={(value) => setFilter({ ...filter, exhibition_status: value, page: 1 })}
            />
          </FormField>

          <FormField label="À Venda" minW="160px" flex="1">
            <SearchableSelect
              isClearable
              placeholder="Todos"
              options={[
                { value: '1', label: 'Sim' },
                { value: '0', label: 'Não' }
              ]}
              value={filter.available_for_auction}
              onChange={(value) => setFilter({ ...filter, available_for_auction: value, page: 1 })}
            />
          </FormField>

          <FormField label="Status Entrega" minW="160px" flex="1">
            <SearchableSelect
              isClearable
              placeholder="Todos"
              options={statusOptions(DELIVERY_STATUS)}
              value={filter.delivery_status}
              onChange={(value) => setFilter({ ...filter, delivery_status: value, page: 1 })}
            />
          </FormField>

          <FormField label="Status Venda" minW="160px" flex="1">
            <SearchableSelect
              isClearable
              placeholder="Todos"
              options={saleStatusOptions}
              value={filter.sale_status}
              onChange={(value) => setFilter({ ...filter, sale_status: value, page: 1 })}
            />
          </FormField>

          <FormField label="Aviso de Retirada" minW="180px" flex="1">
            <SearchableSelect
              isClearable
              placeholder="Todos"
              options={pickupEmailStatusOptions}
              value={filter.pickup_email_status}
              onChange={(value) => setFilter({ ...filter, pickup_email_status: value, page: 1 })}
            />
          </FormField>
        </Flex>

        <Flex mt={4} flex="1" alignItems="center">
          {hasRole('send_email_notifications') && selectedContest && (
            <Flex ml="auto" gap={2}>
              <Tooltip label="Enviar o certificado de expositor a quem teve obra exposta">
                <Button size="sm" variant="outline" onClick={exhibitorCertificatesModal.onOpen}>
                  <Icon name="lni lni-certificate" mr="6px" />
                  Enviar certificados de expositor
                </Button>
              </Tooltip>
              <Tooltip label="Avisar compradores e artistas que as obras já podem ser retiradas">
                <Button size="sm" variant="magenta" onClick={pickupEmailsModal.onOpen}>
                  <Icon name="lni lni-envelope" mr="6px" />
                  Enviar e-mails de retirada
                </Button>
              </Tooltip>
            </Flex>
          )}
        </Flex>
      </Box>

      <TableContainer>
        {/* 17 colunas: só aqui o aperto horizontal e a fonte menor se justificam.
            O padding vertical continua vindo do tema, para a linha ter a mesma altura das outras listagens. */}
        <Table sx={{ 'th, td': { px: 1.5, fontSize: 'xs' } }}>
          <Thead>
            <Tr>
              <Th>Img</Th>
              <Th>Obra</Th>
              <Th>Participante</Th>
              <Th>Concurso</Th>
              <Th>Venda</Th>
              <Th>Status Venda</Th>
              <Th>Lance Inicial</Th>
              <Th>Valor da Moldura</Th>
              <Th>Exposição</Th>
              <Th>Recebimento</Th>
              <Th>Devolução</Th>
              <Th>Entrega</Th>
              <Th>Período de Retirada</Th>
              <Th>Aviso de Retirada</Th>
              <Th>Dias em Atraso</Th>
              <Th>Taxa de Armazenamento</Th>
              <Th>Ações</Th>
            </Tr>
          </Thead>
          <Tbody>
            {artworks.map((art, index) => (
              <Tr key={art.id}>
                <Td>
                  {art.art_image_url ? (
                    <Tooltip
                      label={
                        <Image src={art.art_image_url} alt={art.name} maxW="300px" maxH="300px" objectFit="contain" />
                      }
                      bg="white"
                      p={2}
                      borderRadius="md"
                      shadow="lg"
                    >
                      <Image
                        src={art.art_image_url}
                        alt={art.name}
                        boxSize="56px"
                        objectFit="cover"
                        borderRadius="md"
                        cursor="pointer"
                        onClick={() => {
                          setSelectedArtworkIndex(index)
                        }}
                      />
                    </Tooltip>
                  ) : (
                    <Box boxSize="56px" bg="gray.200" borderRadius="md" />
                  )}
                </Td>
                <Td>
                  <Box maxW="150px">
                    <Text fontWeight="medium" noOfLines={2} fontSize="xs">
                      {art.name}
                    </Text>
                    {/* Listing shows only the questions flagged for it (max 2) */}
                    {resolveArtMeta(art)
                      .filter((meta) => meta.show_in_listing)
                      .slice(0, 2)
                      .map((meta) => (
                        <Text
                          key={meta.key}
                          color={meta.answered ? 'gray.600' : 'gray.400'}
                          fontStyle={meta.answered ? undefined : 'italic'}
                          noOfLines={1}
                          fontSize="xs"
                        >
                          {meta.value}
                        </Text>
                      ))}
                    {/* Segmented work type + concept description (truncated, full text on hover) */}
                    {art.work_type && (
                      <Text variant="caption" noOfLines={1}>
                        Tipo: {art.work_type}
                      </Text>
                    )}
                    {art.work_description && (
                      <Tooltip label={art.work_description}>
                        <Text variant="caption" noOfLines={1} cursor="help">
                          {art.work_description}
                        </Text>
                      </Tooltip>
                    )}
                  </Box>
                </Td>
                <Td>
                  <Flex alignItems="center" gap={1}>
                    <Avatar size="2xs" name={art.applicant_name || ''} src={art.applicant_image_path || ''} />
                    <Text fontSize="xs" maxW="100px" noOfLines={1}>
                      {art.applicant_name}
                    </Text>
                  </Flex>
                </Td>
                <Td>
                  <Text fontSize="xs" maxW="100px" noOfLines={1}>
                    {art.contest_name}
                  </Text>
                </Td>
                <Td>
                  <Badge colorScheme={art.available_for_auction ? 'green' : 'gray'}>
                    {art.available_for_auction ? 'Sim' : 'Não'}
                  </Badge>
                </Td>
                <Td>
                  {(() => {
                    const status = getArtworkSaleStatus(art)
                    return <StatusBadge label={status.label} colorScheme={status.colorScheme} />
                  })()}
                </Td>
                <Td>
                  <Text fontSize="xs">
                    {art.available_for_auction ? formatCurrency(Number(art.starting_bid)) : '–'}
                  </Text>
                </Td>
                <Td>{renderFrameValue(art)}</Td>
                <Td>
                  <Menu>
                    <MenuButton
                      as={Button}
                      rightIcon={<ChevronDownIcon />}
                      size="xs"
                      colorScheme={EXHIBITION_STATUS[art.exhibition_status]?.colorScheme}
                    >
                      {EXHIBITION_STATUS[art.exhibition_status]?.label}
                    </MenuButton>
                    <MenuList minW="140px" py={1}>
                      <MenuItem
                        icon={<Icon name="lni lni-checkmark" size="md" />}
                        onClick={() => handleExhibitionStatusChange(art.id, 'approved')}
                        fontSize="sm"
                        fontWeight="medium"
                        color="green.600"
                        py={2.5}
                        px={4}
                        _hover={{ bg: 'green.50' }}
                      >
                        Aprovar
                      </MenuItem>
                      <MenuItem
                        icon={<Icon name="lni lni-reload" size="md" />}
                        onClick={() => handleExhibitionStatusChange(art.id, 'pending')}
                        fontSize="sm"
                        fontWeight="medium"
                        color="yellow.600"
                        py={2.5}
                        px={4}
                        _hover={{ bg: 'yellow.50' }}
                      >
                        Voltar para Pendente
                      </MenuItem>
                      <MenuItem
                        icon={<Icon name="lni lni-pause" size="md" />}
                        onClick={() => handleExhibitionStatusChange(art.id, 'stand_by')}
                        fontSize="sm"
                        fontWeight="medium"
                        color="orange.600"
                        py={2.5}
                        px={4}
                        _hover={{ bg: 'orange.50' }}
                      >
                        Mover para Stand-by
                      </MenuItem>
                      <MenuItem
                        icon={<Icon name="lni lni-close" size="md" />}
                        onClick={() => handleExhibitionStatusChange(art.id, 'rejected')}
                        fontSize="sm"
                        fontWeight="medium"
                        color="red.600"
                        py={2.5}
                        px={4}
                        _hover={{ bg: 'red.50' }}
                      >
                        Reprovar
                      </MenuItem>
                    </MenuList>
                  </Menu>
                </Td>
                <Td>
                  <DateBlurInput
                    initialValue={art.receipt_date || ''}
                    onSave={(value) => handleReceiptDateUpdate(art, value)}
                  />
                </Td>
                <Td>
                  <ReturnDateDisplay
                    returnDate={art.return_date}
                    artId={art.id}
                    onPickupInfoOpen={(artId) => {
                      setSelectedArtIdForPickup(artId)
                      setSelectedArtReturnDateForPickup(art.return_date)
                      pickupInfoModal.onOpen()
                    }}
                  />
                </Td>
                <Td>
                  <StatusBadge status={art.delivery_status} statuses={DELIVERY_STATUS} />
                </Td>
                <Td>
                  <Text fontSize="xs" whiteSpace="nowrap">
                    <EmptyCell value={formatWithdrawWindow(art.withdraw_start_date, art.withdraw_end_date)} />
                  </Text>
                </Td>
                <Td>{renderPickupEmailStatus(art)}</Td>
                <Td>
                  {art.withdraw_days_late > 0 ? (
                    <Badge colorScheme="red">
                      {art.withdraw_days_late} dia{art.withdraw_days_late !== 1 ? 's' : ''}
                    </Badge>
                  ) : (
                    <EmptyCell />
                  )}
                </Td>
                <Td>
                  {art.withdraw_storage_fee > 0 ? (
                    <Text fontSize="xs" fontWeight="medium" color="red.600">
                      {formatCurrency(art.withdraw_storage_fee)}
                    </Text>
                  ) : (
                    <EmptyCell />
                  )}
                </Td>
                <Td>
                  <Flex gap={1}>
                    <Tooltip label="Gerar Identificador de Obra">
                      <IconButton
                        aria-label="Gerar Identificador de Obra"
                        icon={<Icon name="lni lni-printer" size="md" />}
                        colorScheme="blue"
                        size="xs"
                        variant="outline"
                        isLoading={openingPdfId === art.id}
                        onClick={() => handleOpenArtworkIdentifierPdf(art.id)}
                      />
                    </Tooltip>

                    {hasRole('delete_contest_artworks') && (
                      <Tooltip label="Remover obra">
                        <IconButton
                          aria-label="Remover obra"
                          icon={<Icon name="lni lni-trash-can" size="md" />}
                          colorScheme="red"
                          size="xs"
                          variant="outline"
                          onClick={() => setDeletingArtId(art.id)}
                        />
                      </Tooltip>
                    )}
                  </Flex>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </TableContainer>

      {artworks.length === 0 && (
        <Text textAlign="center" color="gray.500" py={8}>
          Nenhuma obra encontrada.
        </Text>
      )}

      <Paginator
        page={filter.page}
        totalPages={totalPages}
        changePage={(page: number) => {
          setFilter({ ...filter, page })
        }}
        pageSize={filter.page_size}
        onPageSizeChange={(pageSize) => {
          setFilter({ ...filter, page_size: pageSize, page: 1 })
        }}
      />

      <ConfirmDialog
        isOpen={receiptEmailModal.isOpen}
        onClose={() => {
          receiptEmailModal.onClose()
          setPendingReceiptEmailArt(null)
        }}
        onConfirm={handleSendReceiptEmail}
        action="send"
        title="Enviar notificação de recebimento"
        description={`Deseja enviar um e-mail para ${pendingReceiptEmailArt?.applicant_name} notificando o recebimento da obra?`}
        confirmLabel="Enviar"
      />

      <ConfirmDialog
        isOpen={deletingArtId > 0}
        onClose={() => setDeletingArtId(0)}
        onConfirm={handleDeleteArt}
        action="delete"
        entity="obra"
        feminine
      />

      <ArtworkPreviewModal
        isOpen={selectedArtworkIndex !== null}
        onClose={() => setSelectedArtworkIndex(null)}
        imageUrl={selectedArtworkIndex !== null ? (artworks[selectedArtworkIndex]?.art_image_url ?? null) : null}
        imageAlt={selectedArtworkIndex !== null ? artworks[selectedArtworkIndex]?.name : undefined}
        artworkId={selectedArtworkIndex !== null ? artworks[selectedArtworkIndex]?.id : undefined}
        onRotationSaved={(newUrl) => {
          const artworkId = selectedArtworkIndex !== null ? artworks[selectedArtworkIndex]?.id : undefined
          if (artworkId === undefined) return
          setArtworks((prev) => prev.map((a) => (a.id === artworkId ? { ...a, art_image_url: newUrl } : a)))
        }}
        onCropSaved={(newUrl) => {
          const artworkId = selectedArtworkIndex !== null ? artworks[selectedArtworkIndex]?.id : undefined
          if (artworkId === undefined) return
          setArtworks((prev) => prev.map((a) => (a.id === artworkId ? { ...a, art_image_url: newUrl } : a)))
        }}
        onPrev={
          selectedArtworkIndex !== null && selectedArtworkIndex > 0
            ? () => setSelectedArtworkIndex((i) => Math.max(0, (i ?? 1) - 1))
            : undefined
        }
        onNext={
          selectedArtworkIndex !== null && selectedArtworkIndex < artworks.length - 1
            ? () => setSelectedArtworkIndex((i) => Math.min(artworks.length - 1, (i ?? 0) + 1))
            : undefined
        }
        positionLabel={selectedArtworkIndex !== null ? `${selectedArtworkIndex + 1} de ${artworks.length}` : undefined}
      />

      {/* Pickup Info Modal */}
      <PickupInfoModal
        isOpen={pickupInfoModal.isOpen}
        onClose={pickupInfoModal.onClose}
        artId={selectedArtIdForPickup}
        currentReturnDate={selectedArtReturnDateForPickup}
        onReturnDateSaved={fetchArtworks}
        allowEditReturnDate
      />

      {/* Send Pickup Emails Modal */}
      {selectedContest && (
        <ContestsSendPickupEmailsModal
          isOpen={pickupEmailsModal.isOpen}
          onClose={pickupEmailsModal.onClose}
          contestId={selectedContest.id}
          contestName={selectedContest.name}
          onSent={fetchArtworks}
        />
      )}

      {/* Send Exhibitor Certificates Modal */}
      {selectedContest && (
        <ContestsSendExhibitorCertificatesModal
          isOpen={exhibitorCertificatesModal.isOpen}
          onClose={exhibitorCertificatesModal.onClose}
          contestId={selectedContest.id}
          contestName={selectedContest.name}
        />
      )}
    </>
  )
}
